import { useEffect } from "react";
import { Navigate, Outlet, useLocation, useNavigate } from "react-router-dom";
import Sidebar from "@/components/ui/sidebar/sidebar";
import { useAuth } from "@/context/AuthContext";
import { SidebarProvider, useSidebar } from "@/providers/sidebar";
import { usesPageSurface } from "@/routes/page-surface-routes";
import { PageSurface } from "@/shared/components/page-surface";
import {
	hasTelemetryRestrictedNav,
	isTelemetryAllowedPath,
	shouldBlockBackofficeAccess,
} from "@/utils/permissions";

export function AppLayoutContent({ children }: { children: React.ReactNode }) {
	const { collapsed } = useSidebar();
	const { pathname } = useLocation();

	return (
		<div className="min-h-screen bg-gray-50">
			<Sidebar />

			<main
				className={`w-full transition-all duration-300 ${
					collapsed ? "lg:pl-16" : "lg:pl-[296px]"
				}`}
			>
				{usesPageSurface(pathname) ? (
					<PageSurface className="min-h-[calc(100vh-4rem)] lg:min-h-screen">
						{children}
					</PageSurface>
				) : (
					<div className="h-full w-full">{children}</div>
				)}
			</main>
		</div>
	);
}

function PrivateGuard({ children }: { children: React.ReactNode }) {
	const { user, loading, logout } = useAuth();
	const navigate = useNavigate();
	const { pathname } = useLocation();

	useEffect(() => {
		if (!(loading || user)) {
			navigate("/login", { replace: true });
		}
	}, [loading, user, navigate]);

	if (loading || !user) {
		return (
			<div className="flex h-screen items-center justify-center">
				Carregando...
			</div>
		);
	}

	if (user?.role && shouldBlockBackofficeAccess(user.role)) {
		return (
			<div className="flex h-screen flex-col items-center justify-center gap-4 font-semibold">
				<p>Você não tem acesso ao BackOffice do Fleeter</p>

				<button
					className="rounded bg-blue-600 px-4 py-2 text-white transition hover:bg-blue-700"
					onClick={() => {
						logout();

						navigate("/login");
					}}
					type="button"
				>
					Voltar ao Login
				</button>
			</div>
		);
	}

	if (
		hasTelemetryRestrictedNav(user.role) &&
		!isTelemetryAllowedPath(pathname, user.role)
	) {
		return <Navigate replace to="/tracking" />;
	}

	return <>{children}</>;
}

export default function PrivateRoute() {
	return (
		<SidebarProvider>
			<PrivateGuard>
				<AppLayoutContent>
					<Outlet />
				</AppLayoutContent>
			</PrivateGuard>
		</SidebarProvider>
	);
}
